import { uniqueId } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, FormControl, Row } from 'react-bootstrap';
import Field from './Field';
import { appChoicesKeys } from '../../../helpers/constants';

const MODEL_FILTER_OPTIONS = ['All', 'Contract', 'Contact', 'Inventory'];

const fieldModels = field =>
  Array.isArray(field.models) ? field.models : field.model ? [field.model] : [];

const matchesSearch = (field, query) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const haystack = [field.label, field.key, field.type, ...fieldModels(field)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(q);
};

const matchesModelFilter = (field, modelFilter) => {
  if (!modelFilter || modelFilter === 'All') return true;
  return fieldModels(field).includes(modelFilter);
};

const FormBuilder = ({ fields = [], onFieldsChange, onSubmit, disabled }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [modelFilter, setModelFilter] = useState('All');
  const [highlightedUuid, setHighlightedUuid] = useState(null);
  // Keep cards visible after unchecking the filtered model until the filter changes
  const [retainedFieldUuids, setRetainedFieldUuids] = useState([]);

  useEffect(() => {
    setRetainedFieldUuids([]);
  }, [modelFilter]);

  const visibleFields = useMemo(
    () =>
      fields.filter(field => {
        const matches =
          matchesSearch(field, searchQuery) && matchesModelFilter(field, modelFilter);
        return matches || retainedFieldUuids.includes(field.uuid);
      }),
    [fields, searchQuery, modelFilter, retainedFieldUuids]
  );

  // Clear highlight on any click/tap anywhere; delay attach so Add New's click doesn't clear immediately
  useEffect(() => {
    if (!highlightedUuid) return undefined;

    const clearHighlight = () => setHighlightedUuid(null);
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', clearHighlight);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', clearHighlight);
    };
  }, [highlightedUuid]);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      });
    });
  };

  const addField = field => {
    const uuid = uniqueId();
    setSearchQuery('');
    setModelFilter('All');
    setHighlightedUuid(uuid);
    onFieldsChange([...fields, { ...field, models: ['Contract'], uuid }]);
    scrollToBottom();
  };

  const changeField = (uuid, newField) => {
    if (
      modelFilter !== 'All' &&
      !matchesModelFilter(newField, modelFilter) &&
      !retainedFieldUuids.includes(uuid)
    ) {
      setRetainedFieldUuids(prev => [...prev, uuid]);
    }
    onFieldsChange(fields.map(field => (field.uuid === uuid ? newField : field)));
  };

  const deleteField = uuid => {
    if (highlightedUuid === uuid) setHighlightedUuid(null);
    setRetainedFieldUuids(prev => prev.filter(id => id !== uuid));
    onFieldsChange(fields.filter(field => field.uuid !== uuid));
  };

  const hasActiveFilters = Boolean(searchQuery.trim()) || modelFilter !== 'All';

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center mb-2">
        <FormControl
          className="flex-grow-1 mr-2 mb-1"
          style={{ maxWidth: 420 }}
          size="sm"
          placeholder="Search fields..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <div className="d-flex align-items-center ml-auto mb-1">
          <h6 className="mb-0 midFont text-muted mr-3">
            Showing {visibleFields.length}
            {hasActiveFilters ? ` of ${fields.length}` : ''} field
            {visibleFields.length === 1 ? '' : 's'}
          </h6>
          <FormControl
            as="select"
            size="sm"
            style={{ width: 160 }}
            value={modelFilter}
            onChange={e => setModelFilter(e.target.value)}
          >
            {MODEL_FILTER_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option === 'All' ? 'All models' : option}
              </option>
            ))}
          </FormControl>
        </div>
      </div>
      <hr className="my-2" />

      <div style={{ paddingBottom: 88 }}>
        {visibleFields.length > 0 ? (
          <Row>
            {visibleFields.map(field => (
              <Col key={field.uuid} xs={12} lg={6} className="mb-3">
                <Field
                  field={field}
                  highlighted={field.uuid === highlightedUuid}
                  onChange={newField => changeField(field.uuid, newField)}
                  onDelete={() => deleteField(field.uuid)}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <h6 className="text-center p-5 text-muted">
            {fields.length > 0 && hasActiveFilters ? 'No fields match your filters!' : 'Nothing to show!'}
          </h6>
        )}
      </div>

      <div
        className="bg-white border rounded shadow-sm d-flex justify-content-end align-items-center px-3 py-2"
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1020,
          paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))'
        }}
      >
        <Button
          disabled={disabled}
          size="sm"
          variant="success"
          onClick={() =>
            addField({
              label: '',
              key: '',
              type: 'text',
              options: [],
              dropdownOptions: [],
              optionAppChoiceName: appChoicesKeys[0],
              takeOptionsFrom: 'appChoice'
            })
          }
        >
          Add New Field
        </Button>
        <Button disabled={disabled} size="sm" className="ml-2" variant="primary" onClick={onSubmit}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default FormBuilder;
